import React, { useState, useEffect, useCallback, useRef, ChangeEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft,
  AlertCircle,
  Loader,
  Lock,
  Save,
  Upload,
  Image,
  X
} from 'lucide-react';
import { getUserInfo } from '../../utils/auth';
import { CommonUtils } from '../../utils/commonUtils';
import { getBrandDetail } from '../../utils/brand';
import { getApiV1Url } from '../../config/api';

const ALLOWED_LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_LOGO_SIZE_BYTES = 10 * 1024 * 1024;
const MIN_LOGO_DIMENSION = 600;

// Types
interface FormData {
  code: string;
  name: string;
  status: string;
  sortOrder: number;
  logo: File | null;
  existingLogo: string | null;
  description: string;
}

interface ValidationErrors {
  code?: string;
  name?: string;
  status?: string;
  sortOrder?: string;
  logo?: string;
  description?: string;
}

const BrandUpdate: React.FC = () => {
  const navigate = useNavigate();
  const { projectCode, brandCode } = useParams<{ projectCode: string; brandCode: string }>();

  const goToBrandConfig = () => {
    if (projectCode) {
      navigate(`/projects/${projectCode}/brands`);
    } else {
      navigate('/projects');
    }
  };

  // Form state
  const [formData, setFormData] = useState<FormData>({
    code: '',
    name: '',
    status: 'ACTIVE',
    sortOrder: 0,
    logo: null,
    existingLogo: null,
    description: ''
  });

  const [logoPreview, setLogoPreview] = useState<string>('');
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [originalData, setOriginalData] = useState<FormData>({
    code: '',
    name: '',
    status: 'ACTIVE',
    sortOrder: 0,
    logo: null,
    existingLogo: null,
    description: ''
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch brand details from API (중복 호출 방지)
  const isFetchingRef = React.useRef(false);
  
  const fetchBrandDetails = useCallback(async () => {
    if (!brandCode) {
      setError('브랜드 코드가 없습니다.');
      setIsLoading(false);
      return;
    }
    if (!projectCode) {
      setError('프로젝트 코드가 없습니다.');
      setIsLoading(false);
      return;
    }

    // 이미 요청 중이면 중복 호출 방지
    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;
    setIsLoading(true);
    setError('');
    
    try {
      const brandData = await getBrandDetail(projectCode, brandCode);
      
      const newFormData: FormData = {
        code: brandData.code,
        name: brandData.name,
        status: brandData.status,
        sortOrder: brandData.sortOrder || 0,
        logo: null,
        existingLogo: brandData.logo || null,
        description: brandData.description || ''
      };
      
      setFormData(newFormData);
      setOriginalData(newFormData);
      setLogoPreview(brandData.logo || '');
    } catch (err: any) {
      console.error('브랜드 상세 조회 오류:', err);
      setError(err instanceof Error ? err.message : '브랜드 정보를 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [brandCode, projectCode]);

  // Load brand details on component mount
  useEffect(() => {
    if (brandCode) {
      fetchBrandDetails();
    }
  }, [brandCode, fetchBrandDetails]);

  // Track changes
  useEffect(() => {
    const changed = 
      formData.status !== originalData.status ||
      formData.name !== originalData.name ||
      formData.code !== originalData.code ||
      formData.sortOrder !== originalData.sortOrder ||
      formData.description !== originalData.description ||
      formData.logo !== null ||
      formData.existingLogo !== originalData.existingLogo;
    setHasChanges(changed);
  }, [formData, originalData]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'name' && value.length > 30) {
      return; // 브랜드명은 30자 제한
    }
    if (name === 'code' && value.length > 20) {
      return; // 브랜드 코드는 20자 제한
    }
    if (name === 'description' && value.length > 255) {
      return; // 설명은 255자 제한
    }
    
    if (name === 'sortOrder') {
      const numValue = parseInt(value, 10);
      if (isNaN(numValue) || numValue < 1) {
        return;
      }
      setFormData(prev => ({ ...prev, [name]: numValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear error
    if (errors[name as keyof ValidationErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const resetLogoInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle logo upload
  const handleLogoUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      setErrors(prev => ({ ...prev, logo: 'PNG, JPG, JPEG, WEBP 파일만 업로드 가능합니다' }));
      resetLogoInput();
      return;
    }

    if (file.size >= MAX_LOGO_SIZE_BYTES) {
      setErrors(prev => ({ ...prev, logo: '파일 크기는 10MB 미만이어야 합니다' }));
      resetLogoInput();
      return;
    }

    const img = new window.Image();
    const reader = new window.FileReader();
    
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      img.onload = () => {
        if (img.width < MIN_LOGO_DIMENSION || img.height < MIN_LOGO_DIMENSION) {
          setErrors(prev => ({ ...prev, logo: '이미지 크기는 600x600 픽셀 이상이어야 합니다' }));
          resetLogoInput();
        } else {
          setFormData(prev => ({ 
            ...prev, 
            logo: file,
            existingLogo: null  // Remove existing logo when new one is uploaded
          }));
          setLogoPreview(dataUrl);
          setErrors(prev => ({ ...prev, logo: undefined }));
        }
      };
      img.src = dataUrl;
    };
    
    reader.readAsDataURL(file);
  };

  // Remove logo (both existing and new)
  const removeLogo = () => {
    setFormData(prev => ({ 
      ...prev, 
      logo: null,
      existingLogo: null 
    }));
    setLogoPreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Get display logo for preview
  const getDisplayLogo = (): string => {
    if (logoPreview) return logoPreview;
    if (formData.existingLogo) return formData.existingLogo;
    return '';
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = '브랜드 코드를 입력해주세요.';
    } else if (formData.code.length > 20) {
      newErrors.code = '브랜드 코드는 20자 이하여야 합니다.';
    }

    if (!formData.name.trim()) {
      newErrors.name = '브랜드명을 입력해주세요.';
    } else if (formData.name.length > 30) {
      newErrors.name = '브랜드명은 30자 이하여야 합니다.';
    }

    if (!formData.status.trim()) {
      newErrors.status = '상태를 선택해주세요.';
    }

    if (formData.sortOrder < 1) {
      newErrors.sortOrder = '순서는 1 이상이어야 합니다.';
    }

    // Logo is optional for update - no validation needed
    // Description is optional - no validation needed

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    if (!hasChanges) {
      alert('변경된 내용이 없습니다');
      return;
    }

    if (!projectCode || !brandCode) {
      alert('프로젝트 또는 브랜드 정보가 없습니다.');
      return;
    }

    setIsSubmitting(true);

    try {
      // FormData 객체 생성 (파일 업로드를 위해)
      const formDataToSend = new FormData();
      formDataToSend.append('code', formData.code.trim());
      formDataToSend.append('name', formData.name.trim());
      formDataToSend.append('status', formData.status);
      formDataToSend.append('sortOrder', formData.sortOrder.toString());
      formDataToSend.append('description', formData.description);
      
      // 로고 파일이 있는 경우 추가
      if (formData.logo) {
        formDataToSend.append('logo', formData.logo);
      }

      const userInfo = getUserInfo();
      if (!userInfo) {
        throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
      }

      const response = await fetch(
        getApiV1Url(`/projects/${projectCode}/brands/${brandCode}`),
        {
          method: 'POST',
          headers: {
            'X-Account-Code': userInfo.accountCode,
            'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
          },
          body: formDataToSend
        }
      );

      if (!response.ok) {
        const statusCode = response.status;
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = CommonUtils.getHttpErrorMessage(statusCode, errorData);
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('브랜드 수정 성공 - 서버 응답:', result);
      
      alert('브랜드가 성공적으로 수정되었습니다!');
      goToBrandConfig();
    } catch (err: any) {
      console.error('브랜드 수정 오류:', err);
      const displayMessage = err?.message || CommonUtils.getNetworkErrorMessage(err) || '브랜드 수정 중 오류가 발생했습니다.';
      alert(displayMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm('수정사항이 저장되지 않습니다. 취소하시겠습니까?')) {
        goToBrandConfig();
      }
    } else {
      goToBrandConfig();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={handleCancel}
                className="mr-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-semibold text-gray-900">브랜드 수정</h1>
            </div>
            {hasChanges && (
              <span className="text-sm text-orange-600 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                수정사항이 있습니다
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-12">
            <Loader className="w-8 h-8 text-gray-400 mx-auto mb-4 animate-spin" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">브랜드 정보를 불러오는 중...</h3>
            <p className="text-gray-500">잠시만 기다려주세요.</p>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="w-16 h-16 text-red-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">브랜드 정보를 불러올 수 없습니다</h3>
            <p className="text-gray-500 mb-4 whitespace-pre-line text-center max-w-md mx-auto">{error}</p>
            <button 
              onClick={fetchBrandDetails}
              className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              다시 시도
            </button>
          </div>
        )}

        {/* Form Content */}
        {!isLoading && !error && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 sm:p-8">
              {/* Instructions */}
              <div className="mb-8">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">브랜드 정보 수정</h2>
                <p className="text-sm text-gray-600">
                  브랜드의 정보를 수정할 수 있습니다. 브랜드 코드는 변경할 수 없습니다.
                </p>
              </div>

              <div className="space-y-6">
                    {/* Brand Code */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        브랜드 코드 <span className="text-red-500">*</span>
                      </label>
                      <div className="flex items-center">
                        <Lock className="w-5 h-5 text-gray-400 mr-2" />
                        <input
                          type="text"
                          name="code"
                          value={formData.code}
                          onChange={handleInputChange}
                          disabled
                          className={`flex-1 px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-600 cursor-not-allowed ${
                            errors.code ? 'border-red-500' : ''
                          }`}
                          maxLength={20}
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-500">브랜드 코드는 변경할 수 없습니다</p>
                      {errors.code && (
                        <p className="mt-1 text-sm text-red-500 flex items-center">
                          <AlertCircle className="w-4 h-4 mr-1" />
                          {errors.code}
                        </p>
                      )}
                    </div>

                    {/* Brand Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        브랜드명 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="브랜드명 입력 (최대 30자)"
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 ${
                          errors.name ? 'border-red-500' : 'border-gray-300'
                        }`}
                        maxLength={30}
                      />
                      <div className="mt-1 flex items-center justify-between">
                        {errors.name && (
                          <p className="text-sm text-red-500 flex items-center">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            {errors.name}
                          </p>
                        )}
                        <span className="text-xs text-gray-500 ml-auto">
                          {formData.name.length}/30
                        </span>
                      </div>
                    </div>

                    {/* Brand Status */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        상태 <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 ${
                          errors.status ? 'border-red-500' : 'border-gray-300'
                        }`}
                      >
                        <option value="ACTIVE">활성</option>
                        <option value="INACTIVE">비활성</option>
                      </select>
                      {errors.status && (
                        <p className="mt-1 text-sm text-red-500 flex items-center">
                          <AlertCircle className="w-4 h-4 mr-1" />
                          {errors.status}
                        </p>
                      )}
                      <div className="mt-2 flex items-center space-x-3">
                        <div className="flex items-center">
                          <span className={`w-2 h-2 rounded-full mr-2 ${
                            formData.status === 'ACTIVE' ? 'bg-green-500' : 'bg-gray-400'
                          }`} />
                          <span className="text-sm text-gray-600">
                            {formData.status === 'ACTIVE' ? '브랜드가 활성 상태입니다' : '브랜드가 비활성 상태입니다'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Sort Order */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        순서
                      </label>
                      <input
                        type="number"
                        name="sortOrder"
                        value={formData.sortOrder}
                        onChange={handleInputChange}
                        placeholder="순서 입력"
                        min="1"
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 ${
                          errors.sortOrder ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.sortOrder && (
                        <p className="mt-1 text-sm text-red-500 flex items-center">
                          <AlertCircle className="w-4 h-4 mr-1" />
                          {errors.sortOrder}
                        </p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">브랜드 표시 순서를 설정합니다 (1 이상의 숫자)</p>
                    </div>

                    {/* Logo Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        로고 이미지
                      </label>
                      <div className="text-xs text-gray-500 mb-3">
                        PNG, JPG, JPEG, WEBP 파일만 업로드 가능 (최소 600x600 픽셀, 10MB 미만)
                      </div>
                      
                      {!getDisplayLogo() ? (
                        // Upload area when no logo
                        <div
                          onClick={() => fileInputRef.current?.click()}
                          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                            errors.logo
                              ? 'border-red-300 bg-red-50'
                              : 'border-gray-300 hover:border-gray-400 bg-gray-50 hover:bg-gray-100'
                          }`}
                        >
                          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                          <p className="text-sm text-gray-600 mb-1">
                            클릭하여 이미지를 업로드하세요
                          </p>
                          <p className="text-xs text-gray-500">
                            PNG, JPG, JPEG, WEBP • 600x600px 이상 • 10MB 미만
                          </p>
                        </div>
                      ) : (
                        // Display existing or new logo
                        <div className="flex items-start space-x-4">
                          <div className="relative">
                            <img
                              src={getDisplayLogo()}
                              alt="Logo preview"
                              className="w-32 h-32 object-cover rounded-lg border border-gray-300"
                            />
                            <button
                              onClick={removeLogo}
                              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900 mb-1">
                              {formData.logo ? formData.logo.name : '기존 로고'}
                            </p>
                            <p className="text-xs text-gray-500 mb-3">
                              {formData.logo ? `${Math.round(formData.logo.size / 1024)} KB` : '저장된 이미지'}
                            </p>
                            <button
                              onClick={() => fileInputRef.current?.click()}
                              className="text-sm text-blue-600 hover:underline"
                            >
                              다른 이미지 선택
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {errors.logo && (
                        <p className="mt-2 text-sm text-red-500 flex items-center">
                          <AlertCircle className="w-4 h-4 mr-1" />
                          {errors.logo}
                        </p>
                      )}
                      
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        설명
                      </label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="브랜드에 대한 간단한 설명을 입력하세요 (최대 255자)"
                        rows={3}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 resize-none ${
                          errors.description ? 'border-red-500' : 'border-gray-300'
                        }`}
                        maxLength={255}
                      />
                      <div className="mt-1 flex items-center justify-between">
                        {errors.description && (
                          <p className="text-sm text-red-500 flex items-center">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            {errors.description}
                          </p>
                        )}
                        <span className="text-xs text-gray-500 ml-auto">
                          {formData.description.length}/255
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="mt-8 flex items-center justify-end gap-3 pt-6 border-t border-gray-200">
                    <button
                      onClick={handleCancel}
                      disabled={isSubmitting}
                      className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 transition-colors disabled:opacity-50"
                    >
                      취소
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={isSubmitting || !hasChanges}
                      className="px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader className="w-4 h-4 mr-2 animate-spin" />
                          저장 중...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          저장
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
      </div>
    </div>
  );
};

export default BrandUpdate;

